import { ApiService, CustomHttpParams } from '@abb-procure/api';
import { CustomHttpErrorResponse } from '@abb-procure/error-handling';
import { Injectable, inject } from '@angular/core';
import { array as ioArray, boolean as ioBoolean } from 'io-ts';
import { AdditionalInfoModel } from '../models/additional-info.model';
import { CurrentUserModel } from '../models/current-user.model';
import { PeoplePickerUserModel } from '../models/people-picker-user.model';
import { UserDetailsModel } from '../models/user-details.model';
import { UserState } from '../state/user.state';
import { CurrentUser } from '../types/current-user.type';
import { PeoplePickerUser } from '../types/people-picker-user.type';
import { UserDetails } from '../types/user-details.type';
import { User } from '../types/user.type';
import { UsersRequest } from '../types/users-request.type';

@Injectable({
  providedIn: 'root',
})
export class UserApiService extends ApiService {
  private readonly userState = inject(UserState);

  async getUsers(
    usersRequest: UsersRequest,
  ): Promise<PeoplePickerUserModel[] | null> {
    const queryParams = new CustomHttpParams(usersRequest);

    try {
      const response = await this.get<PeoplePickerUser[]>(
        'User/Search',
        ioArray(PeoplePickerUser),
        queryParams,
      );

      return response != null
        ? response.map((user) => new PeoplePickerUserModel(user))
        : null;
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving the users.',
      );
    }
  }

  async getUser(userId?: number): Promise<CurrentUserModel | null> {
    const queryParams = new CustomHttpParams(
      userId
        ? {
            userId,
          }
        : {},
    );

    try {
      const response = await this.get<CurrentUser>('User', User, queryParams);

      return response != null ? new CurrentUserModel(response) : null;
    } catch (error) {
      if (error instanceof CustomHttpErrorResponse && error.status === 401) {
        this.userState.setForbidden(true);

        if (error.error.additionalInfo) {
          this.userState.setAdditionalInfo(
            new AdditionalInfoModel(error.error.additionalInfo),
          );
        }

        if (error.error.message) {
          this.userState.setAdditionalInfoMessage(error.error.message);
        }

        return null;
      }

      return this.catchError(
        error,
        'There was an issue while retrieving the user data.',
      );
    }
  }

  async getUserDetails(userId?: number): Promise<UserDetailsModel | null> {
    const queryParams = new CustomHttpParams(
      userId
        ? {
            userId,
          }
        : {},
    );

    try {
      const response = await this.get<UserDetails>(
        'User/Details',
        UserDetails,
        queryParams,
      );

      return response != null ? new UserDetailsModel(response) : null;
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving the user details.',
      );
    }
  }

  async checkUserSession(): Promise<boolean | null> {
    try {
      return this.get<boolean>('Session/Check', ioBoolean);
    } catch (error) {
      return this.catchError(
        error,
        'There was an issue while retrieving the hubs.',
      );
    }
  }
}
